import type { ShapeSelectionHistoryMeta } from '@/lib/shapes/types/shape-selection-history-meta'
import type {
  ForwardedShapeSelectionHistory,
  PendingSelectionHistoryMeta,
} from '../types/selection-history'

function sameShapeIdSets(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  const sa = [...a].sort()
  const sb = [...b].sort()

  return sa.every((id, i) => id === sb[i])
}

/**
 * True when the shape manager re-reports (within 300ms, with no staged text meta) the same
 * before → after transition that was just forwarded WITH text meta — that replay must be dropped.
 */
export function isDuplicateShapeSelectionReplay({
  pending,
  lastForwarded,
  beforeIds,
  afterIds,
  nowMs,
}: {
  pending: PendingSelectionHistoryMeta | null
  lastForwarded: ForwardedShapeSelectionHistory | null
  beforeIds: string[]
  afterIds: string[]
  nowMs: number
}): boolean {
  return (
    pending == null &&
    lastForwarded?.hadPendingTextMeta === true &&
    nowMs - lastForwarded.atMs < 300 &&
    sameShapeIdSets(lastForwarded.beforeIds, beforeIds) &&
    sameShapeIdSets(lastForwarded.afterIds, afterIds)
  )
}

/** Selected text-box ids to record as the "before" text selection when nothing was staged. */
export function inferBeforeTextIds({
  pending,
  selectedTextBoxIds,
  beforeIds,
  afterIds,
}: {
  pending: PendingSelectionHistoryMeta | null
  selectedTextBoxIds: string[]
  beforeIds: string[]
  afterIds: string[]
}): string[] | undefined {
  return pending == null && selectedTextBoxIds.length > 0 && !sameShapeIdSets(beforeIds, afterIds)
    ? [...selectedTextBoxIds]
    : undefined
}

/** The meta forwarded with the entry: the manager's own, merged with any staged/inferred text meta. */
export function buildForwardedSelectionHistoryMeta(
  meta: ShapeSelectionHistoryMeta | undefined,
  pending: PendingSelectionHistoryMeta | null,
  inferredBeforeTextIds: string[] | undefined
): ShapeSelectionHistoryMeta | undefined {
  return pending || inferredBeforeTextIds
    ? {
        ...meta,
        beforeTextIds: pending?.beforeTextIds ?? inferredBeforeTextIds,
        afterTextIds: pending?.afterTextIds ?? [],
        beforeChrome: pending?.beforeChrome ?? meta?.beforeChrome,
        afterChrome: pending?.afterChrome ?? meta?.afterChrome,
      }
    : meta
}
